import { useState, useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import DealsHero from './components/DealsHero';
import CategoryFilter from './components/CategoryFilter';
import FeaturedDeals from './components/FeaturedDeals';
import DealsGrid from './components/DealsGrid';
import DealsNewsletter from './components/DealsNewsletter';
import { Link } from 'react-router-dom';

export default function DealsPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // SEO meta‑tags
  useSEO({
    title: 'Roseville Deals & Coupons | Exclusive Local Discounts | Roseville.org',
    description: 'Save big with exclusive Roseville, CA deals and coupons. Discounts on dining, shopping, health, home services, and more from 200+ local Roseville businesses. New deals added daily.',
    keywords: 'Roseville deals, Roseville coupons, local discounts Roseville CA, Roseville restaurant deals, Roseville business offers, Placer County savings, Roseville promo codes',
    canonical: '/deals',
    ogImage: 'https://public.readdy.ai/ai/img_res/13e29917811dbeb839b3c5c1e360401f.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Local Deals & Coupons',
          description: 'Exclusive deals and coupons from local Roseville, California businesses.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/deals`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Deals & Coupons', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/deals` },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Where can I find deals and coupons in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Roseville.org features exclusive deals and coupons from 200+ local Roseville businesses including restaurants, retail shops, health services, and home improvement companies. New deals are added daily.',
              },
            },
          ],
        },
      ],
    },
  });

  // Example scroll handler – can be expanded later
  useEffect(() => {
    const handleScroll = () => {
      // placeholder for future scroll‑related logic
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Guard against undefined search queries when passing to child components
  const safeSearchQuery = typeof searchQuery === 'string' ? searchQuery : '';

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <DealsHero searchQuery={safeSearchQuery} onSearchChange={setSearchQuery} />
      
      {/* Add CTA Banner */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white">
              <h3 className="text-2xl font-bold mb-1">Want to Promote Your Deals?</h3>
              <p className="text-emerald-50">Reach 50,000+ local shoppers and boost your sales</p>
            </div>
            <a
              href="/submit-deal"
              className="bg-white text-emerald-600 px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all duration-300 whitespace-nowrap"
            >
              Submit Your Deal
            </a>
          </div>
        </div>
      </div>

      <CategoryFilter
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />
      <FeaturedDeals searchQuery={safeSearchQuery} />
      <DealsGrid activeCategory={activeCategory} searchQuery={safeSearchQuery} />

      {/* Data Sources Attribution */}
      <section className="bg-white pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-teal-600 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Deals & Coupons Data Sources</h3>
                <p className="text-xs text-gray-600 leading-relaxed mb-3">
                  All deals and coupons are sourced directly from participating Roseville businesses and verified for accuracy before publication.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">Deal Information</p>
                    <ul className="text-[10px] text-gray-500 space-y-0.5 leading-relaxed">
                      <li>• Submitted and verified by participating businesses</li>
                      <li>• Pricing and discount amounts confirmed at time of listing</li>
                      <li>• Expiration dates set by the business owner</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">Business Ratings</p>
                    <ul className="text-[10px] text-gray-500 space-y-0.5 leading-relaxed">
                      <li>• Ratings aggregated from verified customer reviews</li>
                      <li>• Claim counts tracked in real-time</li>
                      <li>• Business details cross-referenced with our directory</li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                  <p className="text-[11px] text-gray-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>Data last updated: March 12, 2025
                  </p>
                  <Link to="/data-sources" className="inline-flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer">
                    View All Data Sources <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DealsNewsletter
        formUrl="https://readdy.ai/api/form/d6a29adbiueuoqm1s6gg"
      />
      <Footer />
    </div>
  );
}
